import type { Follow } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActionSubmit,
  Modal,
  ModalBody,
  ModalButton,
  ModalButtons,
  ModalCloseX,
  ModalContentForm,
  ModalHeading,
  ModalTrigger,
  IconButton,
  GearOutlineIcon,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import type { FormEventHandler } from "react"
import {
  useState,
  useCallback,
} from "react"

import styles from "./SleepHoursModal.module.scss"
import {
  SleepHoursInputs,
  DiscardChangesDialog,
} from "src/components"
import {
  useOverlayLoader,
  usePolyglot,
  useFollowedUsers,
} from "src/contexts"
import {
  useUpdateFollow,
  useTrackSleepSettings,
} from "src/hooks"
import { GenericErrorToast } from "src/toasts"

const polyglotPrefix = "pages.alerts.phone_call.sleep_hours_modal."
const saveButtonId = "save-sleep-hours-id"

const defaultState = {
  start_time: "",
  end_time: "",
}

export function SleepHoursModal() {
  const polyglot = usePolyglot()
  const trackSleepSettings = useTrackSleepSettings()

  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const { selfFollow } = useFollowedUsers()

  const [
    isOpen,
    setIsOpen,
  ] = useState(false)

  const [
    isDiscardOpen,
    setIsDiscardOpen,
  ] = useState<boolean>(false)

  const [
    state,
    setState,
  ] = useState<Follow["sleep_settings"]>(defaultState)

  const updateFollow = useUpdateFollow()

  // force closes the modal
  const closeModal = useCallback(
    () => {
      setIsOpen(false)
    },
    [setIsOpen],
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        setIsOverlayLoaderVisible(true)

        try {
          if (!updateFollow) {
            throw new Error("cannot update sleep hours")
          }

          await updateFollow({
            id: selfFollow.id,
            sleep_settings: state,
          })

          trackSleepSettings(state)
          closeModal()
        } catch {
          showToast(
            "save-sleep-hours-toast",
            GenericErrorToast,
          )
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      state,
      selfFollow,
      updateFollow,
      trackSleepSettings,
      showToast,
      closeModal,
      setIsOverlayLoaderVisible,
    ],
  )

  const hasChanges =
    state.start_time !== defaultState.start_time &&
    state.end_time !== defaultState.end_time

  // show the warning dialog if changes have been made without saving
  const onOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && hasChanges) {
        setIsDiscardOpen(true)
        return
      }

      if (newOpen) {
        // reset the state each time the modal opens
        setState(defaultState)
      }

      setIsOpen(newOpen)
    },
    [
      setState,
      setIsOpen,
      setIsDiscardOpen,
      hasChanges,
    ],
  )

  return (
    <Modal
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <ModalTrigger asChild>
        <IconButton>
          <GearOutlineIcon />
        </IconButton>
      </ModalTrigger>
      <ModalContentForm onSubmit={onSubmit}>
        <ModalHeading>
          {polyglot.t(`${polyglotPrefix}heading`)}
          <ModalCloseX />
        </ModalHeading>
        <ModalBody className={styles.modalBody}>
          <div className={styles.description}>
            {polyglot.t(`${polyglotPrefix}description`)}
          </div>
          <SleepHoursInputs onChange={setState} />
          <ModalButtons>
            <ModalButton stylePreset="secondary">
              {polyglot.t(`${polyglotPrefix}cancel_cta`)}
            </ModalButton>
            <ActionSubmit
              className={styles.modalMultiButton}
              stylePreset="primary"
              value={polyglot.t(`${polyglotPrefix}submit_cta`)}
              disabled={!hasChanges}
              id={saveButtonId}
            />
          </ModalButtons>
        </ModalBody>
        <DiscardChangesDialog
          open={isDiscardOpen}
          setOpen={setIsDiscardOpen}
          discardChanges={closeModal}
        />
      </ModalContentForm>
    </Modal>
  )
}
